import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  ExternalLink,
  Wallet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/status-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useMyRegistrations,
  useOutstandingBalance,
  type ManualChargesBalance,
} from '@/api/extensions/useRegistrationOps';
import {
  useInitiateManualCharges,
  useConfirmPayment,
  useMyManualLedger,
  type ManualLedgerEntry,
  type InitiateManualChargesResponse,
} from '@/api/extensions/usePaymentOps';
import { useAuthStore } from '../../state_management_and_stores/auth.store';
import { formatCurrency } from '@/utils/formatters';
import { PayNowDialog } from '@/components/events/PayNowDialog';
import { BulkPayDialog } from '@/components/events/BulkPayDialog';
import { StripeCheckoutDialog } from '@/components/events/StripeCheckoutDialog';
import { ProviderBadge, deriveProvider } from '@/components/financials/ProviderBadge';

/**
 * "Funds" tab on the profile page. Shows the user's registration ledger
 * (own + registrations they created as a buddy-group primary), the total
 * outstanding balance, and lets them settle unpaid rows one by one via
 * the existing PayPal embedded flow. Cancellation lives elsewhere — this
 * view is read-only for state changes other than payment.
 */

interface RefundSummary {
  status: string;
  amount_refunded: number;
  transaction_fee_retained: number;
  entry_fee_refunded: number;
  non_member_fee_refunded: number;
  paid_total: number;
  currency: string;
  refunded_at: string | null;
}

interface RegistrationRow {
  id: string;
  event_id: string;
  user_profile_id: string;
  payment_id?: string | null;
  payment_amount?: number | string | null;
  payment_deadline?: string | null;
  paid_for_partner?: boolean | null;
  // Fee breakdown components attached server-side by getMyRegistrations.
  // entry/transaction come from the events row; non_member_fee from the
  // registration row. All are per-side (not doubled) — the helper
  // computes the doubling effect when paid_for_partner is true.
  event_entry_fee?: number | null;
  event_transaction_fee?: number | null;
  non_member_fee?: number | string | null;
  non_member?: boolean | null;
  // Partner-side fee components — surfaced ONLY for paid_for_partner
  // primary rows. Without these, the breakdown was doubling the
  // primary's own non_member_fee, which mis-reported the row total
  // when the two sides had different membership statuses (e.g. primary
  // is a member but partner isn't — primary side $0, partner side $25,
  // displayed total was $0 × 2 = $0 instead of $25).
  partner_payment_amount?: number | null;
  partner_non_member_fee?: number | null;
  payment_provider?: string | null;
  /**
   * Method the user picked at registration time — 'stripe', 'cash', or
   * 'pay_later' (legacy 'paypal' values may also appear on older rows).
   * Nullable for very old rows created before the column existed; the
   * Funds tab falls back to a safe label in that case.
   */
  payment_method?: string | null;
  /**
   * Stripe-side method type — 'card' | 'us_bank_account' | other. Captured
   * from charge.payment_method_details.type by the webhook handler and the
   * optimistic /complete path. Drives the "Visa •••• 4242" /
   * "Bank (ACH) •••• 6789" label on the Funds tab.
   */
  payment_method_type?: string | null;
  /**
   * Trimmed summary of the method used — brand + last4 for cards,
   * bank_name + last4 for ACH. Mirror of the same field on
   * payments.metadata.
   */
  payment_method_details?: {
    type?: string | null;
    brand?: string | null;
    bank_name?: string | null;
    last4?: string | null;
    funding?: string | null;
    account_type?: string | null;
  } | null;
  payment_status?: string | null;
  payment_paid_amount?: number | null;
  payment_currency?: string | null;
  /**
   * ACH bank-verification (microdeposits) pending: the linked bank payment is
   * in Stripe `requires_action` and within the ~10-day verification window. No
   * debit has been initiated, so the entry is unpaid/due — but the user can
   * complete it via Stripe's hosted verification page. Set by the backend
   * enrichment only while the window is open; after that the row reads as a
   * plain unpaid entry (Pay Now supersedes the stale PaymentIntent).
   */
  ach_verification_pending?: boolean | null;
  /** Stripe hosted verification URL the "Verify bank account" link points to. */
  ach_verification_url?: string | null;
  /** Split tender: amount of this entry settled with org account credit
   *  (alongside the Stripe/cash/check charge). Drives the "$X credit + $Y card"
   *  breakdown in the expanded row. */
  credit_applied?: number | null;
  payment_is_bulk?: boolean | null;
  /** Set on rows where the linked payment was captured by someone other
   *  than the row owner (e.g. the primary's PayPal capture covering a
   *  partner). Drives the "paid by Aaron Barnes" label in the funds tab. */
  payment_payer_user_profile_id?: string | null;
  payment_payer_display_name?: string | null;
  /** Most-recent refund summary for this registration's linked payment. */
  refund_summary?: RefundSummary | null;
  /** Set on cancelled rows by the backend enrichment. */
  cancelled_by_user_profile_id?: string | null;
  cancelled_by_display_name?: string | null;
  cancelled_by_is_row_owner?: boolean | null;
  /**
   * Backend enrichment of `created_by` so the funds tab can render
   * "Created by <buddy primary>" on rows the row owner didn't actually
   * create themselves (on-behalf registrations).
   */
  created_by_user_profile_id?: string | null;
  created_by_display_name?: string | null;
  created_by_is_row_owner?: boolean | null;
  updated_by?: string | null;
  trig_event_name?: string | null;
  trig_event_type_name?: string | null;
  trig_rodeo_name?: string | null;
  trig_organization_name?: string | null;
  trig_status_name?: string | null;
  trig_status?: string | null;
  trig_display_name?: string | null;
  trig_user_full_name?: string | null;
  trig_first_name?: string | null;
  trig_last_name?: string | null;
  position?: string | null;
  partner_name?: string | null;
  registration_type?: string | null;
  buddy_group_id?: string | null;
  buddy_group_name?: string | null;
  is_primary_managed_view?: boolean;
  /**
   * Distinct from `is_primary_managed_view`: this row is in my buddy
   * group AND I actually created it on behalf of the teammate (as
   * opposed to the teammate self-registering). Drives the Funds tab
   * inclusion + the "On-behalf" badge — self-registered teammates
   * aren't my financial responsibility even if I'm their buddy primary.
   */
  is_created_on_behalf?: boolean;
  /**
   * True for rows the user paid for but doesn't own (i.e. the partner
   * row of a paid_for_partner team registration). Surfaced on the funds
   * tab so the user keeps a full audit trail of what they paid for —
   * including a now-cancelled partner seat with a refund record on it.
   */
  paid_by_me?: boolean;
  created_at?: string | null;
  [key: string]: unknown;
}

interface FeeBreakdown {
  entry: number;
  transaction: number;
  nonMember: number;
  /**
   * The flat partner-swap adjustment fee (PARTNER_SWAP_ADJUSTMENT_FEE,
   * currently $2) — only ever non-zero on a partner-pay primary row or a
   * team-partner row whose partner was swapped after the original capture.
   * On every other row this is 0 and any leftover gap has been folded into
   * `transaction` instead (it can only be the Stripe processing fee there).
   */
  swapFee: number;
  total: number;
  /** True when this registration's seat is being covered by a partner-pay flow. */
  paidForPartner: boolean;
  /**
   * True ONLY for legacy paid_for_partner rows where the doubling lives on
   * the registration's payment_amount instead of being split across two
   * bulk-linked rows. Drives the "× 2" suffixes in the line items — for
   * new bulk-style rows the per-side amounts already represent one share,
   * so suffixing × 2 would mislead the user into thinking THIS row is the
   * doubled total.
   */
  legacyDoubled: boolean;
  /**
   * True only when BOTH sides of a paid_for_partner pair contributed a
   * non-member surcharge. Per-line suffix decision: entry + transaction
   * are always doubled, but non-member surcharge is asymmetric — a
   * member primary covering a non-member partner means the $25
   * surcharge came from one seat, not two. "× 2" is misleading there.
   */
  nonMemberDoubled: boolean;
}

/**
 * Compute the per-row fee breakdown from the columns the backend attaches.
 *
 * For paid_for_partner rows (the primary side of a team registration where
 * the primary covered the partner's seat), the displayed total is ALWAYS
 * the doubled per-side amount — that's what the user was actually charged
 * via PayPal, and the audit trail must reflect the original charge even
 * after the partner row has cancelled. The bulk-vs-legacy storage layout
 * differs (bulk: $135 stored on each row; legacy: $270 stored on primary)
 * but the user-facing total is identical. Compute from the raw fee
 * components so display is robust regardless of which storage shape the
 * row uses.
 *
 * For non-paid_for_partner rows (single-row registrations, partner-side
 * rows), no doubling — each row reflects its own per-side cost.
 */
export function computeFeeBreakdown(
  row: RegistrationRow,
  // When `unpaid`, the row hasn't been captured yet so its stored
  // payment_amount is fee-EXCLUDED (entry-only — a pay-later seat, or an
  // abandoned ACH attempt that waived the fee). Price it card-inclusive by
  // default (entry + effective card transaction fee + surcharge) so the
  // breakdown matches what Pay Now charges; the fee is only dropped if the
  // contestant actually picks ACH at checkout.
  opts?: { unpaid?: boolean },
): FeeBreakdown {
  const paidForPartner = row.paid_for_partner === true;
  const multiplier = paidForPartner ? 2 : 1;
  const entry = (row.event_entry_fee ?? 0) * multiplier;
  // The backend's `event_transaction_fee` (a per-event override, or the
  // platform-config-derived Stripe fee) is only a HINT here — the actual
  // transaction fee shown is DERIVED from the captured total below, so the
  // line items always sum to the Total no matter how/when the fee was
  // charged (e.g. a non-member surcharge added in a second capture with
  // its own fixed fee, an entry fee edited after payment, a changed fee
  // config). The hint is used only to split txn vs. swap fee on rows that
  // can carry a partner-swap adjustment.
  let transaction = (row.event_transaction_fee ?? 0) * multiplier;
  // A partner-swap adjustment fee can only land on a partner-pay primary
  // row (its total = primary + partner halves, and the partner half was
  // bumped by the flat fee at reinvite time) or on a team-partner row
  // (whose own payment_amount carries that bump). On any other row there
  // is no such fee — so on those rows the whole residual is transaction fee.
  const canHaveSwapFee =
    paidForPartner || row.registration_type === 'team_partner';

  // Non-member fee CAN'T be naively `primary.non_member_fee × 2` for
  // paid_for_partner rows — the primary and partner can have different
  // membership statuses. The backend now surfaces partner_non_member_fee
  // explicitly so we add per-side amounts. Falls back to the legacy
  // doubling when the partner field is absent (older payloads without
  // the enrichment).
  const primaryNonMember = parseFloat(String(row.non_member_fee ?? 0)) || 0;
  const partnerNonMember = paidForPartner ? row.partner_non_member_fee ?? null : null;
  let nonMember: number;
  let nonMemberDoubled: boolean;
  if (paidForPartner) {
    if (partnerNonMember != null) {
      nonMember = primaryNonMember + (partnerNonMember || 0);
      // "× 2" only when BOTH sides actually contributed. A member primary
      // paying for a non-member partner has the $25 charge come from
      // one seat — labelling the row "× 2" would imply $12.50 each.
      nonMemberDoubled = primaryNonMember > 0 && (partnerNonMember || 0) > 0;
    } else {
      // Legacy fallback when partner data didn't make it onto the row.
      // Stick with the historical "× 2" since we have no way to tell
      // whether sides were symmetric.
      nonMember = primaryNonMember * 2;
      nonMemberDoubled = primaryNonMember > 0;
    }
  } else {
    nonMember = primaryNonMember;
    nonMemberDoubled = false;
  }

  const stored = parseFloat(String(row.payment_amount ?? 0)) || 0;
  const partnerStored = paidForPartner
    ? row.partner_payment_amount ?? 0
    : 0;
  const computed = entry + transaction + nonMember;

  // For paid_for_partner rows the stored payment_amount on EACH row is
  // the per-side amount; sum gives the team total. After a partner-swap
  // reinvite, the partner row's payment_amount is bumped by the flat
  // swap fee at link time, so the sum naturally reflects the post-swap
  // total ($135 + $137 = $272 in a non-member-replacing-member swap).
  // This matches what the originating user agreed to pay in the swap-
  // approval dialog and is consistent across cancel-modal and dashboard
  // displays. Falls back to entry+tx+fees or the stored value for
  // legacy rows missing the partner enrichment.
  // Unpaid rows: use the card-priced `computed` total (entry + effective card
  // fee hint + surcharge) instead of the fee-excluded stored amount, so the
  // breakdown shows the transaction fee the contestant will actually pay by
  // card. Paid rows keep deriving from the captured `stored` total so the line
  // items always reconcile to what was charged.
  const total = opts?.unpaid
    ? computed
    : paidForPartner
      ? row.partner_payment_amount != null
        ? stored + partnerStored
        : computed > 0
          ? computed
          : stored
      : stored > 0
        ? stored
        : computed;
  // The transaction fee is whatever was captured beyond entry + surcharge
  // (and minus the flat partner-swap adjustment on swap-capable rows).
  // Derive it from the captured total so entry + transaction + nonMember +
  // swapFee always equals the Total — robust to the surcharge being added
  // in a separate capture, an entry fee edited after payment, etc.
  const residual = Math.max(0, Math.round((total - entry - nonMember) * 100) / 100);
  let swapFee = 0;
  if (canHaveSwapFee) {
    // residual = per-side txn fee(s) + the flat partner-swap adjustment.
    // The backend's recomputed per-side fee anchors the split; clamp it so
    // it can never exceed what was actually captured.
    transaction = Math.min(Math.max(0, transaction), residual);
    swapFee = Math.round((residual - transaction) * 100) / 100;
  } else {
    transaction = residual;
  }
  return {
    entry,
    transaction,
    nonMember,
    swapFee,
    total,
    paidForPartner,
    legacyDoubled: paidForPartner,
    nonMemberDoubled,
  };
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getRegistrantName(row: RegistrationRow): string {
  return (
    row.trig_display_name ||
    row.trig_user_full_name ||
    [row.trig_first_name, row.trig_last_name].filter(Boolean).join(' ') ||
    'Unknown'
  );
}

/**
 * Per-row payment state. Three concepts get conflated otherwise:
 *   - `unpaid` — the row owes money (regardless of whether the rodeo has
 *     run yet, so we don't accidentally tell the user "paid" for a
 *     pending_payment row on an upcoming rodeo).
 *   - `due_now` — the row is in the backend's outstanding-balance set,
 *     which only flags fees as enforceable once the parent rodeo has
 *     FINISHED. Drives the Pay Now button.
 *   - `paid` — confirmed and not owing.
 */
type RowPaymentState =
  | 'paid'
  | 'unpaid_due'
  | 'unpaid_not_due'
  // Online payment (typically ACH/bank) submitted to Stripe but not yet
  // settled. The contestant has paid — funds are clearing — so this is NOT
  // a payable state: no Pay Now button, no due-now bucket.
  | 'processing'
  | 'no_fee'
  | 'cancelled';

/**
 * Status-filter chip identifiers. `all` is the exclusive "show everything"
 * option; the rest are additive bucket selections.
 *   - paid: confirmed and settled (or free / no-fee rows)
 *   - pending: anything pending payment — pre- AND post-entry-close.
 *     Collapsed into one chip because the pre/post distinction was
 *     confusing (two chips that both meant "you owe money" but landed
 *     rows in different buckets depending on entry-close timing).
 *   - cancelled: status=cancelled, kept in history
 */
type FilterStatus = 'all' | 'paid' | 'pending' | 'cancelled';

const STATUS_FILTER_LABELS: Record<FilterStatus, string> = {
  all: 'All',
  paid: 'Paid',
  pending: 'Pending',
  cancelled: 'Cancelled',
};

const STATUS_FILTER_ORDER: FilterStatus[] = [
  'all',
  'paid',
  'pending',
  'cancelled',
];

export function deriveRowPaymentState(
  row: RegistrationRow,
  // Registration_ids the backend marked as actually due right now
  // (post-entry-close pending fees + supplemental rejection
  // surcharges). Per-row instead of per-event so multi-reg events
  // where two rows for the same event_id sit in different states
  // classify correctly.
  dueNowRegistrationIds: Set<string>,
  // Registration_ids the backend reports as unpaid in any state
  // (due-now OR pre-entry-close pay-later). Used to mark
  // confirmed-but-uncaptured-PayPal rows as `unpaid_due` without
  // collapsing onto event-level matching.
  unpaidRegistrationIds: Set<string>,
): RowPaymentState {
  const status = row.trig_status_name || row.trig_status || '';
  const fee = parseFloat(String(row.payment_amount ?? 0)) || 0;

  // Cancelled registrations carry no obligation regardless of how the
  // backend classifies them. Without this gate a stray entry in the
  // outstanding-balance set would drag a cancelled row into
  // 'unpaid_due' and surface it to the bulk-pay flow which would 400
  // on "cancelled status — only pending_payment / confirmed
  // registrations can be paid online".
  if (status === 'cancelled') return 'cancelled';

  // An in-flight online payment (ACH/bank debit still clearing) means the
  // contestant has already paid — we're just waiting on settlement. Surface
  // it as 'processing' BEFORE the pending/unpaid branches so it never shows a
  // Pay Now button (which would double-charge) or land in the due-now bucket.
  if (row.payment_status === 'processing') return 'processing';

  // Nothing owed, nothing captured → free (either a $0 event or an admin
  // waive). This MUST precede the status branches: `pending_payment` is a
  // denormalised field, and a row whose fees were zeroed but whose status the
  // backend hasn't re-derived yet would otherwise render as a $0 obligation
  // complete with a Pay Now button. Gated on the backend's own unpaid set, so
  // a row it still considers collectible (confirmed-but-uncaptured PayPal, a
  // post-rejection supplemental surcharge) is never written off here.
  if (fee <= 0 && !row.payment_id && !unpaidRegistrationIds.has(row.id)) {
    return 'no_fee';
  }

  if (status === 'pending_payment') {
    // Pre-entry-close pay-later seats are unpaid_not_due (owed but
    // not yet enforceable); post-entry-close are unpaid_due.
    return dueNowRegistrationIds.has(row.id) ? 'unpaid_due' : 'unpaid_not_due';
  }
  // Partner-pending team-roping primaries (status='pending_confirmation_and_payment'
  // or 'pending_confirmation') are usually NOT settled — the team isn't
  // complete yet. Without this branch they fell through to the 'paid'
  // default, which painted them with a green PAID badge on the funds tab
  // while the admin entries ledger correctly read them as unpaid. Treat as
  // unpaid_not_due — the obligation is real.
  //
  // The exception is a seat paid BEFORE its partner accepted: that's allowed
  // (the fee is owed either way), and the capture clears only the money bit,
  // leaving the row on 'pending_confirmation' with a completed payment. It
  // must read as paid, or the tab would re-offer Pay Now on an entry that has
  // already been charged.
  if (
    status === 'pending_confirmation_and_payment' ||
    status === 'pending_confirmation'
  ) {
    if (row.payment_status === 'completed') return 'paid';
    return dueNowRegistrationIds.has(row.id) ? 'unpaid_due' : 'unpaid_not_due';
  }
  // Confirmed-but-uncaptured PayPal AND confirmed-with-supplemental
  // (post-rejection surcharge) both show up in the unpaid set; flag
  // them as due-now since they're collectible right away.
  if (unpaidRegistrationIds.has(row.id)) return 'unpaid_due';
  return 'paid';
}

/**
 * Derive a human-readable payment method label. Prefers the explicit
 * `payment_method` column the backend now persists at registration time —
 * one of 'paypal' / 'cash' / 'pay_later' — and falls back to status +
 * payment_id heuristics for legacy rows that pre-date the column.
 *
 * When the linked payment's payer is NOT the row owner (i.e. the primary
 * covered the partner's seat), append "· paid by <name>" so the partner
 * knows whose PayPal account actually settled the entry — without this,
 * John seeing "PayPal" on his own row gives no hint that Aaron paid.
 */
export function describePaymentMethod(
  row: RegistrationRow,
  state: RowPaymentState,
): string {
  const status = row.trig_status_name || row.trig_status || '';
  const payerSuffix = row.payment_payer_display_name
    ? ` · paid by ${row.payment_payer_display_name}`
    : '';
  // Cancelled rows that captured money before being cancelled should still
  // read as the original method ("Card or Bank" / "Cash") — the funds
  // landed. Treat 'cancelled' the same as 'paid' for label purposes; the
  // actual cancelled badge lives on the row's status pill.
  const settled = state === 'paid' || state === 'cancelled';
  // Explicit method takes precedence — distinguishes cash from pay_later
  // (and from a pending-capture online row), which is impossible from row
  // state alone. The online-pay sentinel is 'stripe'; the legacy 'paypal'
  // value is treated identically — historical rows from the PayPal era
  // and any in-flight clients on the back-compat coerce path land here.
  // For Stripe rows we know the *actual* method used — card brand + last4
  // for cards, bank_name + last4 for ACH. Build the specific label first,
  // and fall back to "Card or Bank" only when the type isn't known yet.
  const stripeLabel = (() => {
    const t = row.payment_method_type ?? row.payment_method_details?.type;
    if (!t) return null;
    if (t === 'card') {
      const brand = row.payment_method_details?.brand;
      const last4 = row.payment_method_details?.last4;
      const brandLabel = brand
        ? brand.charAt(0).toUpperCase() + brand.slice(1)
        : 'Card';
      return last4 ? `${brandLabel} •••• ${last4}` : brandLabel;
    }
    if (t === 'us_bank_account') {
      const last4 = row.payment_method_details?.last4;
      const bank = row.payment_method_details?.bank_name;
      // ACH explicit because the settlement timing is meaningfully
      // different from card — users who see "Bank" should know it's
      // delayed (4 business days).
      const base = bank ? `${bank} (ACH)` : 'Bank (ACH)';
      return last4 ? `${base} •••• ${last4}` : base;
    }
    // Stripe added a method we don't have a hand-tuned label for
    // (e.g. cashapp). Surface the type verbatim so it's still
    // distinguishable instead of showing the generic fallback.
    return t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ');
  })();

  // A 'processing' payment is ALWAYS an in-flight ACH/bank debit — cards
  // settle instantly and never sit in 'processing'. Surface it explicitly as
  // ACH (regardless of the stored payment_method string or whether the method
  // detail arrived) so it's never mislabelled the generic "Card or Bank" and
  // the contestant understands a bank transfer is clearing — not that a fresh
  // payment is still owed. Handled before the method switch so every code
  // path (stripe, paypal, legacy null-method) reads consistently.
  if (state === 'processing') {
    const isBank =
      (row.payment_method_type ?? row.payment_method_details?.type) ===
      'us_bank_account';
    const base =
      isBank && stripeLabel ? `${stripeLabel} — Processing` : 'ACH Processing';
    return `${base}${payerSuffix}`;
  }

  // ACH bank-account verification pending (microdeposits): UNLIKE a
  // 'processing' debit (handled above) the bank debit has NOT started — no
  // money is in flight, the entry is unpaid/due, and the user must verify
  // their bank within Stripe's ~10-day window. Label it distinctly so the
  // method column agrees with the row's "Bank verification needed" badge and
  // isn't confused with either a clearing debit or a plain unpaid charge.
  // Runs before the method switch (like the processing branch) so stripe /
  // legacy-null rows read consistently.
  if (row.ach_verification_pending) {
    const base = stripeLabel
      ? `${stripeLabel} — Verification needed`
      : 'Bank (ACH) — Verification needed';
    return `${base}${payerSuffix}`;
  }

  switch (row.payment_method) {
    case 'cash':
      return settled
        ? `Cash${payerSuffix}`
        : 'Cash at event';
    case 'check':
      // Admin-recorded offline check payment — always a settled state.
      return `Check${payerSuffix}`;
    case 'pay_later':
      return 'Pay later';
    case 'stripe': {
      // In-flight ACH ('processing') is handled above; here `state` is a
      // settled capture or an uncaptured/pay-later online charge. Mark the
      // latter "(pending)" so the contestant knows money is still owed.
      const base = stripeLabel
        ? settled
          ? stripeLabel
          : `${stripeLabel} (pending)`
        : settled
          ? 'Card or Bank'
          : 'Card or Bank (pending)';
      return `${base}${payerSuffix}`;
    }
    case 'paypal': {
      // Legacy rows captured through the old PayPal flow. Surface them
      // as "PayPal" so the historical capture is identifiable in the
      // funds list — the actual money came through PayPal, not Stripe.
      const base = settled ? 'PayPal' : 'PayPal (pending)';
      return `${base}${payerSuffix}`;
    }
  }
  // Legacy fallback (payment_method IS NULL) — preserve the old behavior
  // for rows that pre-date the column. New online captures land with
  // payment_method='stripe' so this branch only fires for null-method rows
  // that happened to acquire a payment_id later.
  if (status === 'pending_payment') return 'Pay later';
  if (row.payment_id) {
    const base = settled ? 'Card or Bank' : 'Card or Bank (pending)';
    return `${base}${payerSuffix}`;
  }
  if (status === 'cancelled' || status === 'rejected') return '—';
  return state === 'paid' ? `No fee${payerSuffix}` : '—';
}

/**
 * Which slice of the consolidated Funds view to render. The profile page
 * now splits the former single "Funds" tab into two tabs and reuses this
 * one component for both so the intricate balance / payment-state logic
 * lives in exactly one place:
 *   - `events`     → just the Entry history list (the "Event History" tab).
 *   - `financials` → the account-balance card, manual charges, and the
 *                    breakdown dialog (the "Financials" tab; the transaction
 *                    ledger is composed alongside it on the page).
 *   - `all`        → everything in one column (legacy / standalone usage).
 */
export type PaymentsTabView = 'events' | 'financials' | 'all';

export function PaymentsTab({ view = 'all' }: { view?: PaymentsTabView } = {}) {
  const showBalance = view !== 'events';
  const showEntries = view !== 'financials';
  const navigate = useNavigate();
  const { profile } = useAuthStore();
  const myProfileId = (profile as any)?.id as string | undefined;

  const { data: registrations, isLoading: regLoading } = useMyRegistrations({
    include_buddy_group: true,
  });
  const { data: outstanding, isLoading: balanceLoading } =
    useOutstandingBalance();

  // Detailed line-item breakdown dialog. Driven by registrations + the
  // outstanding-balance enrichment so the user can audit every charge,
  // refund, and outstanding fee with status + date filters.
  // Per-row Pay Now: opens PayNowDialog for a single registration.
  const [singlePayId, setSinglePayId] = useState<string | null>(null);
  // Pay All: one PayPal capture covering many rows via BulkPayDialog. Hold
  // a snapshot of the ids at the time the dialog opened so a row landing
  // mid-flight doesn't change what's being paid for.
  const [bulkPayIds, setBulkPayIds] = useState<string[] | null>(null);
  // Manual-charge ids that ride along on the bulk-pay capture so the same
  // Stripe charge settles event-registration debt + admin-recorded
  // ledger debt in one transaction. Populated alongside bulkPayIds when
  // routePayment is asked to include them; null otherwise.
  const [bulkManualIds, setBulkManualIds] = useState<string[] | undefined>(
    undefined,
  );
  // Status filter — multi-select toggle group above the Registration
  // history list. `all` is exclusive (clears the other selections). Empty
  // selection collapses back to `all` so the list never goes blank from
  // filter state alone. Defaults to "pending" only — the bucket the user
  // is most likely landing on the funds page to act on; paid/cancelled
  // rows stay one click away.
  const [statusFilters, setStatusFilters] = useState<Set<FilterStatus>>(
    () => new Set<FilterStatus>(['pending']),
  );
  const toggleStatusFilter = (status: FilterStatus) => {
    setStatusFilters((prev) => {
      // "All" is exclusive — selecting it wipes the rest. Re-clicking it
      // is a no-op (still "all"); if you want to narrow, click another
      // chip and "all" gets dropped automatically.
      if (status === 'all') return new Set<FilterStatus>(['all']);
      const next = new Set(prev);
      if (next.has('all')) {
        return new Set<FilterStatus>([status]);
      }
      if (next.has(status)) {
        next.delete(status);
        if (next.size === 0) return new Set<FilterStatus>(['all']);
      } else {
        next.add(status);
      }
      return next;
    });
  };
  // Rows the user has expanded. All entries default to collapsed to
  // keep the list scannable; clicking a row's header or chevron adds
  // it to this set, clicking again removes it.
  const [toggledRowIds, setToggledRowIds] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleRow = (id: string) => {
    setToggledRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Backend's full unpaid-balance picture, broken out into the structures
  // the page needs:
  //
  //   - `dueNowRegistrationIds` — the set of registration_ids the backend
  //     considers collectible right now (rows with `is_due_now=true`:
  //     post-entry-close pending fees + supplemental rejection surcharges).
  //     Used to split unpaidRows into "Due now" vs "Pay-later" buckets
  //     and to drive the per-row state badge.
  //
  //   - `unpaidRegistrationIds` — every registration_id the backend
  //     reports unpaid (regardless of due-now). Used by
  //     `deriveRowPaymentState` so the UI's "Pending" classification
  //     matches the backend exactly even on multi-reg events where the
  //     same event_id holds multiple rows in different states.
  //
  //   - `owedByRegistrationId` — per-row `amount_owed` map. For
  //     supplemental rows (post-rejection surcharge on already-paid
  //     PayPal registrations) this is the diff (e.g. $25 of $80
  //     because $55 was already captured); for pending rows it's the
  //     full payment_amount. Used to override `computeFeeBreakdown`
  //     so the Funds page totals never over-bill what the user
  //     actually paid.
  const dueNowRegistrationIds = useMemo(() => {
    const set = new Set<string>();
    for (const e of outstanding?.unpaid_events ?? []) {
      if (!e.registration_id) continue;
      // Treat a missing flag as due-now (back-compat for older
      // backend builds where every returned entry was implicitly due).
      if (e.is_due_now !== false) set.add(e.registration_id);
    }
    return set;
  }, [outstanding]);
  const unpaidRegistrationIds = useMemo(() => {
    const set = new Set<string>();
    for (const e of outstanding?.unpaid_events ?? []) {
      if (e.registration_id) set.add(e.registration_id);
    }
    return set;
  }, [outstanding]);
  const owedByRegistrationId = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of outstanding?.unpaid_events ?? []) {
      if (e.registration_id) {
        map.set(e.registration_id, e.amount_owed);
      }
    }
    return map;
  }, [outstanding]);

  const rows: RegistrationRow[] = useMemo(() => {
    const list = (registrations ?? []) as RegistrationRow[];
    // The backend returns every buddy-group teammate row when
    // include_buddy_group=true (event-detail page needs that roster).
    // For the Funds tab we only want rows that are part of THIS user's
    // financial story: their own, or a teammate row the primary actually
    // created on behalf, or a partner row whose seat the primary paid for.
    // Teammate rows the teammate self-created (and self-paid) are visible
    // elsewhere — they don't belong on the primary's Funds page.
    const financiallyMine = list.filter(
      (r) =>
        r.user_profile_id === myProfileId ||
        r.is_created_on_behalf === true ||
        r.paid_by_me === true,
    );
    // Cancelled rows STAY in the funds tab — they're history. The user
    // wants to see what was registered, what was paid, and what was
    // refunded, all in one place. Sorted newest-first by created_at so
    // recent activity sits at the top.
    return financiallyMine.slice().sort((a, b) => {
      const at = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bt - at;
    });
  }, [registrations, myProfileId]);

  // Apply the status-filter chips to the sorted rows. Lives downstream of
  // `rows` so that totals/Pay All math (which uses `rows` and `unpaidRows`)
  // stays correct against the full ledger — the filter is purely a view
  // layer concern.
  const visibleRows = useMemo(() => {
    if (statusFilters.has('all')) return rows;
    return rows.filter((r) => {
      const status = r.trig_status_name || r.trig_status || '';
      if (status === 'cancelled') return statusFilters.has('cancelled');
      const state = deriveRowPaymentState(r, dueNowRegistrationIds, unpaidRegistrationIds);
      // 'processing' rows group under the Paid chip — the contestant has paid;
      // the bank debit is just clearing. They're never in the "still owe" set.
      if (state === 'paid' || state === 'no_fee' || state === 'processing') {
        return statusFilters.has('paid');
      }
      // Both pre-entry-close (unpaid_not_due) and post-entry-close
      // (unpaid_due) rows map to "Pending" — the user wants one chip for
      // "still owe".
      if (state === 'unpaid_due' || state === 'unpaid_not_due') {
        return statusFilters.has('pending');
      }
      return false;
    });
  }, [rows, statusFilters, dueNowRegistrationIds, unpaidRegistrationIds]);

  // Every unpaid row owned by the current user, regardless of whether the
  // rodeo's entries have closed yet. The backend's `/pay-now/:id` endpoint
  // happily accepts pre-entry-close pending_payment rows, so the user CAN
  // settle early — and they expect "Total owed" to reflect everything they
  // actually owe, not just what's collectible right now.
  const unpaidRows = useMemo(
    () =>
      rows.filter((r) => {
        if (r.user_profile_id !== myProfileId) return false;
        // A row mid-ACH-verification owes money but must NOT join the bulk
        // pay-all basket: settling it by card would supersede the pending bank
        // verification. The contestant pays it via the per-row "Verify bank"
        // link or the guarded per-row Pay Now instead. It still renders in the
        // list (visibleRows) and counts toward the backend's total_owed.
        if (r.ach_verification_pending) return false;
        const state = deriveRowPaymentState(r, dueNowRegistrationIds, unpaidRegistrationIds);
        return state === 'unpaid_due' || state === 'unpaid_not_due';
      }),
    [rows, dueNowRegistrationIds, unpaidRegistrationIds, myProfileId],
  );

  // Per-row owed amount. For supplemental rows (post-rejection
  // surcharge on already-paid PayPal registrations) the backend reports
  // a partial-owe — use THAT directly. For all other unpaid rows fall
  // back to `computeFeeBreakdown` so paid_for_partner doubling still
  // works for pre-fix rows that landed with per-side amounts.
  const owedForRow = useCallback(
    (r: RegistrationRow): number => {
      const override = owedByRegistrationId.get(r.id);
      if (override != null) return override;
      return computeFeeBreakdown(r).total;
    },
    [owedByRegistrationId],
  );

  // Totals come straight from the backend so the badge in the header
  // bar, the Funds page TOTAL OWED line, and the DUE NOW box are
  // always in lock-step (they all read the same fields off the
  // outstanding-balance endpoint). Local row-level math is kept only
  // for the bulk-pay / per-row UI affordances.
  const totalOwed = outstanding?.total_owed ?? 0;
  const dueNowAmount = outstanding?.total_due_now ?? 0;
  // Bank (ACH) amount the contestant owes but is actively verifying — part of
  // total owed, but NOT payable through the bulk "pay outstanding balance"
  // flow (paying again by card would supersede the pending verification). It's
  // carved out of the "Not yet due" bucket below so that bucket's money figure
  // matches its (verifying-excluded) row count.
  const achInFlightAmount = outstanding?.total_ach_in_flight ?? 0;
  const notYetDueAmount = Math.max(
    0,
    totalOwed - dueNowAmount - achInFlightAmount,
  );

  // Split the unpaid rows by per-row `is_due_now` (mapped through
  // `dueNowRegistrationIds`). Drives which rows go into the bulk-pay
  // basket when the user clicks "Pay due now" vs "Pay all". The
  // event_id-based split previously used here was wrong on multi-reg
  // events where the same event_id holds rows in different states.
  const dueNowRows = useMemo(
    () => unpaidRows.filter((r) => dueNowRegistrationIds.has(r.id)),
    [unpaidRows, dueNowRegistrationIds],
  );
  const notYetDueRows = useMemo(
    () => unpaidRows.filter((r) => !dueNowRegistrationIds.has(r.id)),
    [unpaidRows, dueNowRegistrationIds],
  );

  // Route payment between bulk and single. Bulk now handles mixed
  // pending_payment + confirmed-with-supplemental rows in one capture
  // (the backend's createBulkPayPalOrder records each row's mode +
  // owed amount in metadata.per_registration so the capture path
  // confirms pending rows + posts per-row supplemental payments for
  // confirmed ones). A single row still goes through the single
  // flow; same path handles confirmed-with-supplemental on its own.
  // Manual-charge adjustment ids that should ride along on the next
  // bulk-pay invocation. Pulled fresh from the outstanding-balance
  // payload each render so we always pay whatever's currently unsettled.
  const manualAdjustmentIdsToPay = useMemo(
    () => outstanding?.manual_charges?.adjustment_ids ?? [],
    [outstanding?.manual_charges?.adjustment_ids],
  );

  const routePayment = (
    registrationIds: string[],
    opts?: { includeManual?: boolean },
  ) => {
    const manualIds = opts?.includeManual ? manualAdjustmentIdsToPay : [];
    if (registrationIds.length === 0 && manualIds.length === 0) return;
    // Single-row path only makes sense when there's one registration AND
    // no manual rows tagging along — PayNowDialog is registration-only,
    // can't carry adjustment ids. Force the bulk path whenever manual
    // ids are present so the unified capture works.
    if (registrationIds.length === 1 && manualIds.length === 0) {
      setSinglePayId(registrationIds[0]);
      return;
    }
    setBulkPayIds(registrationIds);
    setBulkManualIds(manualIds.length > 0 ? manualIds : undefined);
  };

  const startPayAll = () => {
    routePayment(unpaidRows.map((r) => r.id), { includeManual: true });
  };

  /**
   * Settle just the past-due (due-now) bucket. Used when the user has
   * BOTH past-due fees and pre-entry-close not-yet-due fees and only wants
   * to unblock new registrations without prepaying the pre-entry-close
   * seats. Manual charges always come along — they're flat committee
   * debt with no entry-close grace, so the user is paying them now too.
   */
  const startPayDueNow = () => {
    routePayment(dueNowRows.map((r) => r.id), { includeManual: true });
  };

  const startPayOne = (registrationId: string) => {
    setSinglePayId(registrationId);
  };

  // Pay Now entry point that interposes a warning when the entry's current
  // payment is an ACH still awaiting bank microdeposit verification. Paying
  // with a different method SUPERSEDES that pending ACH — the backend cancels
  // the unverified PaymentIntent on completion so the customer can't later
  // verify the old microdeposit and be charged twice. A plain unpaid row
  // skips the warning and opens the dialog directly.
  const [verifyWarnId, setVerifyWarnId] = useState<string | null>(null);
  const requestPayOne = (row: RegistrationRow) => {
    if (row.ach_verification_pending) {
      setVerifyWarnId(row.id);
      return;
    }
    startPayOne(row.id);
  };
  const verifyWarnRow = useMemo(
    () =>
      verifyWarnId ? rows.find((r) => r.id === verifyWarnId) ?? null : null,
    [rows, verifyWarnId],
  );

  const closeBulk = () => {
    setBulkPayIds(null);
    setBulkManualIds(undefined);
  };
  const closeSingle = () => setSinglePayId(null);

  const singlePayRow = useMemo(
    () => (singlePayId ? rows.find((r) => r.id === singlePayId) : null),
    [rows, singlePayId],
  );
  const bulkPayTotal = useMemo(() => {
    if (!bulkPayIds) return null;
    const idSet = new Set(bulkPayIds);
    const regSum = rows
      .filter((r) => idSet.has(r.id))
      .reduce((sum, r) => sum + owedForRow(r), 0);
    const manualSum =
      bulkManualIds && bulkManualIds.length > 0
        ? outstanding?.manual_charges?.net_owed ?? 0
        : 0;
    return regSum + manualSum;
  }, [bulkPayIds, bulkManualIds, rows, owedForRow, outstanding?.manual_charges?.net_owed]);
  // Sum of transaction fees baked into the bulk-pay total. Surfaced in the
  // dialog so the user knows part of "Total due" won't come back if they
  // later cancel a covered registration.
  const bulkPayTransactionFee = useMemo(() => {
    if (!bulkPayIds) return null;
    const idSet = new Set(bulkPayIds);
    return rows
      .filter((r) => idSet.has(r.id))
      .reduce((sum, r) => sum + computeFeeBreakdown(r, { unpaid: true }).transaction, 0);
  }, [bulkPayIds, rows]);
  // Same thing for the single-row Pay Now flow.
  const singlePayTransactionFee = useMemo(() => {
    if (!singlePayRow) return null;
    return computeFeeBreakdown(singlePayRow, { unpaid: true }).transaction;
  }, [singlePayRow]);

  const isLoading = regLoading || balanceLoading;

  // Manual-charge balance is folded into the Account-balance card's
  // total so the user sees ONE "you owe $X" number that covers both
  // registration debt and admin-recorded ledger charges. Per-bucket
  // displays (Due now / Not yet due / Manual charges) still split out
  // their respective sources so the user can see the breakdown.
  const manualOwed = outstanding?.manual_charges?.net_owed ?? 0;
  const combinedTotalOwed = totalOwed + manualOwed;

  return (
    <div className="space-y-6">
      {showBalance && (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            Account balance
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Outstanding fees from your registrations and any
            committee-recorded charges.
          </p>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {isLoading ? (
            <Skeleton className="h-24 w-full rounded-lg" />
          ) : combinedTotalOwed === 0 ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className="bg-green-100 text-green-800 border-green-200"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" />
                All caught up
              </Badge>
              <span className="text-sm text-muted-foreground">
                No outstanding fees.
              </span>
            </div>
          ) : (
            <>
              {/* Bucket breakdown so the user can tell at a glance which
                  fees are blocking new registrations (Due now), which are
                  pre-entry-close pay-later seats they can settle early
                  but aren't required to (Not yet due), and which are
                  flat committee-recorded charges that have to be paid
                  separately via the manual-charges card below (Manual
                  charges). */}
              <div
                className={`grid grid-cols-1 ${
                  manualOwed > 0 && (dueNowAmount > 0 || notYetDueAmount > 0)
                    ? 'sm:grid-cols-3'
                    : 'sm:grid-cols-2'
                } gap-3`}
              >
                {dueNowAmount > 0 && (
                  <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-4">
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-rose-700 dark:text-rose-300">
                      Due now
                    </p>
                    <p className="text-2xl font-bold text-rose-700 dark:text-rose-300 mt-1">
                      {formatCurrency(dueNowAmount, 2)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      {dueNowRows.length}{' '}
                      registration{dueNowRows.length === 1 ? '' : 's'} from{' '}
                      {dueNowRows.length === 1 ? 'a rodeo' : 'rodeos'}{' '}
                      that {dueNowRows.length === 1 ? 'has' : 'have'} already
                      finished.{' '}
                      <span className="font-medium text-foreground">
                        Must be paid before you can register for any new
                        events.
                      </span>
                    </p>
                  </div>
                )}
                {notYetDueAmount > 0 && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-amber-700 dark:text-amber-300">
                      Not yet due
                    </p>
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300 mt-1">
                      {formatCurrency(notYetDueAmount, 2)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      {notYetDueRows.length}{' '}
                      registration{notYetDueRows.length === 1 ? '' : 's'}{' '}
                      for {notYetDueRows.length === 1 ? 'a rodeo' : 'rodeos'}{' '}
                      that {notYetDueRows.length === 1 ? "hasn't" : "haven't"}{' '}
                      run yet. Fees aren't required until the
                      rodeo is over — you can settle early if you want, but
                      they don't block new registrations.
                    </p>
                  </div>
                )}
                {manualOwed > 0 && (
                  <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide font-semibold text-rose-700 dark:text-rose-300">
                        Manual charges
                      </p>
                      <Badge
                        variant="outline"
                        className="text-[10px] uppercase tracking-wide bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40"
                      >
                        Due now
                      </Badge>
                    </div>
                    <p className="text-2xl font-bold text-rose-700 dark:text-rose-300 mt-1">
                      {formatCurrency(manualOwed, 2)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      {outstanding?.manual_charges?.unsettled.length ?? 0}{' '}
                      charge
                      {(outstanding?.manual_charges?.unsettled.length ?? 0) === 1
                        ? ''
                        : 's'}{' '}
                      recorded by an admin against your account.{' '}
                      <span className="font-medium text-foreground">
                        Must be paid before you can register for new events
                        with the organization that filed each charge.
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Total + CTA strip. Two buttons appear when the user has
                  BOTH past-due fees and pre-entry-close not-yet-due fees,
                  so they can unblock new registrations by paying just the
                  past-due bucket without prepaying their pre-entry-close
                  seats. When only one bucket has a balance, we collapse
                  to a single contextually-labeled button. */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Total owed
                  </p>
                  <p className="text-xl font-bold text-foreground">
                    {formatCurrency(combinedTotalOwed, 2)}
                  </p>
                  {manualOwed > 0 && totalOwed > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {formatCurrency(totalOwed, 2)} registrations +{' '}
                      {formatCurrency(manualOwed, 2)} manual
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  {unpaidRows.length > 0 && (
                    <>
                      {dueNowRows.length > 0 && notYetDueRows.length > 0 && (
                        <Button
                          onClick={startPayDueNow}
                          size="sm"
                          variant="default"
                        >
                          <CircleDollarSign className="h-4 w-4 mr-1" />
                          Pay due now ({formatCurrency(dueNowAmount, 2)})
                        </Button>
                      )}
                      <Button
                        onClick={startPayAll}
                        size="sm"
                        variant={
                          dueNowRows.length > 0 && notYetDueRows.length > 0
                            ? 'outline'
                            : 'default'
                        }
                      >
                        <CircleDollarSign className="h-4 w-4 mr-1" />
                        {dueNowRows.length > 0 && notYetDueRows.length > 0
                          ? `Pay everything (${formatCurrency(totalOwed, 2)})`
                          : 'Pay outstanding balance'}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                {dueNowRows.length > 0 && notYetDueRows.length > 0 ? (
                  <>
                    <span className="font-medium text-foreground">
                      Pay due now
                    </span>{' '}
                    settles only the {dueNowRows.length} past-due{' '}
                    registration{dueNowRows.length === 1 ? '' : 's'} so you
                    can register for new events.{' '}
                    <span className="font-medium text-foreground">
                      Pay everything
                    </span>{' '}
                    additionally pre-pays your {notYetDueRows.length}{' '}
                    pre-entry-close seat{notYetDueRows.length === 1 ? '' : 's'}{' '}
                    in the same checkout. One transaction, one capture.
                  </>
                ) : (
                  <>
                    Paying the outstanding balance settles all{' '}
                    {unpaidRows.length}{' '}
                    registration{unpaidRows.length === 1 ? '' : 's'} in a
                    single checkout. One transaction, one capture.
                  </>
                )}
              </p>
              {/* In-flight ACH note: the verifying amount is part of Total owed
                  but excluded from the bulk pay action above, so explain why the
                  payable figure is smaller and how to settle it. */}
              {achInFlightAmount > 0 && (
                <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1">
                  {formatCurrency(achInFlightAmount, 2)} of your total is a bank
                  (ACH) entry that's still verifying. It's included in what you
                  owe but can't be paid here while verification is in progress —
                  finish verifying your bank, or use{' '}
                  <span className="font-medium">Pay Now</span> on that entry
                  below to cancel it and pay by card instead.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}

      {showEntries && (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Entry history</CardTitle>
          <p className="text-sm text-muted-foreground">
            Every entry you've made — including ones you set up for
            buddy-group members. Click a row to open the event.
          </p>
          {/* Status filter chips. Defaults to Paid + Owed; user can toggle
              additional buckets, or click All to clear the narrowing. */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Show
            </span>
            {STATUS_FILTER_ORDER.map((status) => {
              const active = statusFilters.has(status);
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => toggleStatusFilter(status)}
                  aria-pressed={active}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-border hover:border-primary/50'
                  }`}
                >
                  {STATUS_FILTER_LABELS[status]}
                </button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title="No entries yet"
              description="Once you enter an event, it will show up here."
              action={{
                label: 'Browse Jackpots',
                onClick: () => navigate('/jackpots'),
              }}
            />
          ) : visibleRows.length === 0 ? (
            // The user has registrations, but none match the active filter
            // chips. Make the empty state actionable instead of dead-end —
            // one click switches back to "All" so they can find what they
            // were looking for.
            <EmptyState
              icon={Wallet}
              title="No entries match"
              description="Try a different filter to see more of your entry history."
              action={{
                label: 'Show all',
                onClick: () =>
                  setStatusFilters(new Set<FilterStatus>(['all'])),
              }}
            />
          ) : (
            <div className="space-y-3">
              {visibleRows.map((row) => {
                const paymentState = deriveRowPaymentState(
                  row,
                  dueNowRegistrationIds,
                  unpaidRegistrationIds,
                );
                // Pay button shows for any unpaid row the user owns. The
                // backend `/pay-now/:id` endpoint accepts pre-entry-close
                // rows, so we don't gate by due-now — users can settle early.
                // EXCEPT when the row isn't draw-eligible (partner declined,
                // legacy partnerless team-roping, etc.): paying then would
                // lock in fees against a row the draw will refuse to include.
                //
                // Gate on partner_status AND draw_eligible. partner_status
                // catches the explicit needs_partner flag; draw_eligible
                // catches every other shape ('none' / 'cancelled' /
                // 'rejected' on a team-event row) so an orphan can't slip
                // through just because its partner_status didn't get
                // migrated to 'needs_partner'.
                // Pay Now is gated to the two unpaid states ONLY. An in-flight
                // ACH/bank debit (paymentState === 'processing') is excluded by
                // construction: deriveRowPaymentState short-circuits a
                // payment_status='processing' row to 'processing' before any
                // unpaid classification, so it can never satisfy this guard.
                // That exclusion is the safety-critical bit — the contestant
                // has already paid and a second payment would double-charge —
                // and TypeScript now enforces it (the unpaid-state narrowing
                // makes a stray 'processing' comparison a compile error).
                // A cancelled rodeo is never payable: the payment endpoints
                // refuse it, and offering the button would only dead-end.
                const rodeoCancelled = (row as any).rodeo_cancelled === true;
                const canPay =
                  (paymentState === 'unpaid_due' ||
                    paymentState === 'unpaid_not_due') &&
                  !rodeoCancelled &&
                  row.user_profile_id === myProfileId &&
                  (row as any).partner_status !== 'needs_partner' &&
                  (row as any).draw_eligible !== false;
                const status =
                  row.trig_status_name || row.trig_status || 'pending';
                // Prefer the user-entered event_name; fall back to the
                // event_type label (e.g. "Open #10 Roping") when the event
                // doesn't carry a custom name. Without this fallback, rows
                // for events that were never given a custom name render as
                // a generic "Event" — making the funds card useless for
                // identifying which entry the row belongs to.
                const eventName =
                  (row.trig_event_name && row.trig_event_name.trim()) ||
                  (row.trig_event_type_name && row.trig_event_type_name.trim()) ||
                  'Event';
                const rodeoName = row.trig_rodeo_name || '';
                const orgName = row.trig_organization_name || '';
                const fees = computeFeeBreakdown(row, {
                  unpaid:
                    paymentState === 'unpaid_due' ||
                    paymentState === 'unpaid_not_due',
                });
                const registrant = getRegistrantName(row);
                // Creator: when a buddy primary registers a teammate, the row
                // is owned by the teammate but the actor is the primary —
                // surface that explicitly so the row owner knows who clicked
                // register. Source the creator from `created_by_*` fields
                // (enriched server-side) rather than the row owner, so the
                // teammate's view doesn't mis-label the row "You".
                // Only label as "You (buddy primary)" when this row is
                // actually here because the primary registered the
                // teammate themselves. A self-registered teammate row
                // (which never reaches the Funds tab after the
                // upstream filter, but the same shape renders elsewhere)
                // shouldn't claim the primary as creator.
                //
                // The "(buddy primary)" suffix on the row-owner branches
                // below is gated on `row.buddy_group_id` so the label
                // matches *current* buddy-group state. Once the row owner
                // declines the buddy invite or otherwise leaves the group,
                // buddy_group_id is cleared (FK ON DELETE SET NULL +
                // explicit clears in the decline/leave flows), so the
                // funds tab stops claiming a now-defunct primary
                // relationship while still preserving the creator's name
                // for audit purposes.
                const createdByIsActiveBuddyPrimary = Boolean(
                  row.buddy_group_id,
                );
                const createdBy = row.is_created_on_behalf
                  ? 'You (buddy primary)'
                  : row.created_by_user_profile_id === myProfileId
                  ? 'You'
                  : row.created_by_is_row_owner
                  ? registrant
                  : row.created_by_display_name
                  ? createdByIsActiveBuddyPrimary
                    ? `${row.created_by_display_name} (buddy primary)`
                    : row.created_by_display_name
                  : createdByIsActiveBuddyPrimary
                  ? 'Buddy primary'
                  : 'Unknown';
                const paymentMethod = describePaymentMethod(row, paymentState);
                // Stripe / HighCall "how was this handled" badge — only once
                // money has actually moved (paid, or cancelled after a
                // capture). Pay-later / unsettled rows resolve to null so we
                // don't imply a settlement that hasn't happened.
                const provider =
                  paymentState === 'paid' || paymentState === 'cancelled'
                    ? deriveProvider(row.payment_method, {
                        hasStripeMethodType: !!(
                          row.payment_method_type ||
                          row.payment_method_details?.type
                        ),
                      })
                    : null;

                const isCancelled = status === 'cancelled';
                // All rows start collapsed; the user opts into details
                // per-row via the chevron or by clicking the header.
                const expanded = toggledRowIds.has(row.id);
                const showPinkBg = canPay; // active + owes money
                const containerClasses = isCancelled
                  ? 'border-dashed bg-muted/30 hover:border-muted-foreground/40'
                  : showPinkBg
                  ? 'border-rose-200 bg-rose-50/70 dark:border-rose-900/40 dark:bg-rose-950/20 hover:border-rose-300 dark:hover:border-rose-800'
                  : 'hover:border-primary/30';
                // Label hierarchy:
                //   - "You" when the viewer is the canceller (highest priority).
                //   - The canceller's display name when we know it (covers
                //     both row-owner cancels — e.g. John on Aaron's
                //     "you paid" view — and buddy-primary cancels).
                //   - Generic fallbacks only when no name resolves.
                const cancelledByLabel = isCancelled
                  ? row.cancelled_by_user_profile_id === myProfileId
                    ? 'You'
                    : row.cancelled_by_display_name
                    ? row.cancelled_by_is_row_owner
                      ? row.cancelled_by_display_name
                      : `${row.cancelled_by_display_name} (buddy primary)`
                    : row.cancelled_by_is_row_owner
                    ? getRegistrantName(row)
                    : 'Buddy primary'
                  : null;
                return (
                  <div
                    key={row.id}
                    className={`rounded-xl border transition-colors ${containerClasses}`}
                  >
                    {/* Header row — always visible. Click to toggle expand;
                        the View event link / Pay Now button stop propagation
                        so they don't accidentally toggle. */}
                    <div
                      className="p-4 cursor-pointer"
                      onClick={() => toggleRow(row.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleRow(row.id);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-sm truncate">
                              {eventName}
                            </h3>
                            <StatusBadge
                              status={status}
                              variant="registration"
                            />
                            {row.position && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal"
                              >
                                {row.position}
                              </Badge>
                            )}
                            {/* Provider badge — quick "handled via Stripe vs
                                recorded by HighCall" signal the user asked to
                                keep front-and-center on each entry. */}
                            {provider && <ProviderBadge provider={provider} />}
                            {/* Payment badges only when they add info beyond
                                the registration status. Cancelled rows defer
                                to CancellationSummary below. */}
                            {paymentState === 'paid' && !isCancelled && (
                              <StatusBadge
                                status="paid"
                                variant="payment"
                              />
                            )}
                            {/* In-flight bank/ACH payment: the contestant has
                                paid; the bank debit is still clearing (~4
                                business days). Labelled "ACH Processing" (not
                                generic "Payment processing") since a settling
                                Stripe payment is always an ACH/bank debit —
                                cards clear instantly. This badge lives in the
                                always-visible header, so it surfaces the ACH
                                state in BOTH the collapsed and expanded rows.
                                No Pay Now is offered for these. */}
                            {paymentState === 'processing' && !isCancelled && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal bg-sky-50 text-sky-700 border-sky-200"
                              >
                                ACH Processing
                              </Badge>
                            )}
                            {/* The producer cancelled the rodeo. Takes the
                                place of any "due" badge — the entry owes
                                nothing and there is no deadline to meet. */}
                            {rodeoCancelled && !isCancelled && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal bg-rose-50 text-rose-800 border-rose-200"
                              >
                                Rodeo cancelled — nothing owed
                              </Badge>
                            )}
                            {paymentState === 'unpaid_not_due' &&
                              !isCancelled &&
                              !rodeoCancelled && (
                                <Badge
                                  variant="outline"
                                  className="text-xs font-normal bg-amber-50 text-amber-800 border-amber-200"
                                >
                                  {/* Team-roping primaries whose team isn't
                                      complete owe money but the charge can't
                                      land until a partner accepts — labelling
                                      these as "Due after the rodeo" hid
                                      that the action item is finding a
                                      partner, not waiting for the deadline. */}
                                  {(status === 'pending_confirmation_and_payment' ||
                                    status === 'pending_confirmation') &&
                                  (row as any).partner_status === 'needs_partner'
                                    ? 'Awaiting partner'
                                    : status === 'pending_confirmation_and_payment' ||
                                      status === 'pending_confirmation'
                                    ? 'Awaiting partner approval'
                                    : 'Due after the rodeo'}
                                </Badge>
                              )}
                            {/* ACH bank-verification pending: the bank debit
                                hasn't started — the user must verify their
                                account (microdeposits) within Stripe's ~10-day
                                window. Shown alongside the unpaid badge; the
                                "Verify bank" action sits in the row actions. */}
                            {row.ach_verification_pending && !isCancelled && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal bg-amber-50 text-amber-800 border-amber-200"
                              >
                                Bank verification needed
                              </Badge>
                            )}
                            {isCancelled &&
                            row.refund_summary?.amount_refunded ? (
                              <StatusBadge
                                status="refunded"
                                variant="payment"
                              />
                            ) : null}
                            {row.is_created_on_behalf && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal bg-blue-50 text-blue-700 border-blue-200"
                              >
                                On-behalf
                              </Badge>
                            )}
                            {row.paid_by_me && !row.is_created_on_behalf && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal bg-violet-50 text-violet-700 border-violet-200"
                              >
                                You paid
                              </Badge>
                            )}
                            {/* Partner row that's being covered by the
                                primary, but the primary hasn't paid yet
                                (Pay Later flow, or PayPal mint without
                                capture). The partner doesn't owe anything
                                personally, but their seat ISN'T secured
                                until the primary settles. Surface the
                                pending-team-payment state explicitly so
                                they don't think a "confirmed" badge means
                                the team is paid. */}
                            {row.registration_type === 'team_partner' &&
                              !row.paid_by_me &&
                              !isCancelled &&
                              fees.total > 0 &&
                              row.payment_status !== 'completed' &&
                              row.payment_status !== 'processing' && (
                                <Badge
                                  variant="outline"
                                  className="text-xs font-normal bg-amber-50 text-amber-800 border-amber-200"
                                >
                                  Awaiting partner&apos;s payment
                                </Badge>
                              )}
                          </div>
                          {(rodeoName || orgName) && (
                            <p className="text-xs text-muted-foreground">
                              {rodeoName}
                              {rodeoName && orgName && (
                                <span className="mx-1.5 opacity-50">·</span>
                              )}
                              {orgName && (
                                <span className="font-medium">{orgName}</span>
                              )}
                            </p>
                          )}
                          {/* Compact summary line in collapsed state — gives
                              the user enough context to decide whether to
                              expand without the full grid + breakdown. */}
                          {!expanded && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                              <span>
                                {formatDateTime(row.created_at)}
                              </span>
                              <span className="opacity-50">·</span>
                              <span className="font-medium text-foreground">
                                {fees.total > 0
                                  ? formatCurrency(fees.total, 2)
                                  : '—'}
                              </span>
                              {isCancelled && cancelledByLabel && (
                                <>
                                  <span className="opacity-50">·</span>
                                  <span>
                                    Cancelled by {cancelledByLabel}
                                  </span>
                                </>
                              )}
                              {isCancelled &&
                                row.refund_summary?.amount_refunded
                                  ? (
                                    <>
                                      <span className="opacity-50">·</span>
                                      <span>
                                        {formatCurrency(
                                          row.refund_summary.amount_refunded,
                                          2,
                                        )}{' '}
                                        refunded
                                      </span>
                                    </>
                                  )
                                  : null}
                            </p>
                          )}
                        </div>
                        <div
                          className="flex items-center gap-2 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.ach_verification_pending &&
                            row.ach_verification_url && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() =>
                                  window.open(
                                    row.ach_verification_url!,
                                    '_blank',
                                    'noopener,noreferrer',
                                  )
                                }
                              >
                                Verify bank
                              </Button>
                            )}
                          {canPay && (
                            <Button
                              size="sm"
                              variant={
                                row.ach_verification_pending
                                  ? 'outline'
                                  : 'default'
                              }
                              onClick={() => requestPayOne(row)}
                            >
                              Pay Now
                            </Button>
                          )}
                          {/* Open event sits in the header action area so it
                              stays reachable when the row is collapsed —
                              users can jump to the event without expanding
                              first. */}
                          {row.event_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2 text-xs"
                              onClick={() => navigate(`/events/${row.event_id}`)}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Open event
                            </Button>
                          )}
                          {/* Expand/collapse toggle — explicit affordance so
                              the click target is clear. The wrapping row body
                              also toggles, but having a chevron makes the
                              action discoverable. */}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                            onClick={() => toggleRow(row.id)}
                          >
                            {expanded ? (
                              <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                    {expanded && (
                      <div className="px-4 pb-4 -mt-1 space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5 text-xs">
                          <Field
                            label="Entered"
                            value={formatDateTime(row.created_at)}
                          />
                          <Field label="Entrant" value={registrant} />
                          <Field label="Created by" value={createdBy} />
                          <Field label="Method" value={paymentMethod} />
                          {row.partner_name && (
                            <Field
                              label="Partner"
                              value={row.partner_name}
                            />
                          )}
                          {row.buddy_group_name && (
                            <Field
                              label="Buddy group"
                              value={row.buddy_group_name}
                            />
                          )}
                          {isCancelled && cancelledByLabel && (
                            <Field
                              label="Cancelled by"
                              value={cancelledByLabel}
                            />
                          )}
                        </div>
                        <FeeBreakdownLine
                          fees={fees}
                          owedOverride={owedByRegistrationId.get(row.id) ?? null}
                        />
                        {/* Split tender — show how the entry was settled when
                            part was covered by account credit. */}
                        {(row.credit_applied ?? 0) > 0 && (
                          <div className="text-xs space-y-0.5 pt-1.5 mt-1.5 border-t border-dashed">
                            <div className="font-medium text-foreground/80 mb-0.5">
                              Paid with
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-muted-foreground">
                                Account credit
                              </span>
                              <span className="ml-auto font-medium text-emerald-700 dark:text-emerald-400">
                                {formatCurrency(row.credit_applied ?? 0, 2)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-muted-foreground">
                                {paymentMethod}
                              </span>
                              <span className="ml-auto font-medium">
                                {formatCurrency(
                                  Math.max(
                                    0,
                                    fees.total - (row.credit_applied ?? 0),
                                  ),
                                  2,
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                        {isCancelled && (
                          <CancellationSummary row={row} fees={fees} />
                        )}
                        {/* Partial-refund line for an active row whose
                            partner cancelled. Shows the bulk-payment
                            aggregate refund (e.g. $130 came back when
                            John cancelled) so Aaron's still-active $270
                            charge has visible context for what was
                            returned. Skipped for cancelled rows — those
                            already get the full CancellationSummary. */}
                        {!isCancelled &&
                          row.refund_summary &&
                          row.refund_summary.amount_refunded > 0 && (
                            <div className="text-xs space-y-0.5 pt-1.5 mt-1.5 border-t border-dashed">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="font-medium text-foreground/80">
                                  Partner-cancellation refund
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-muted-foreground">
                                  Refunded
                                </span>
                                <span className="ml-auto font-medium text-green-700 dark:text-green-400">
                                  {formatCurrency(
                                    row.refund_summary.amount_refunded,
                                    2,
                                  )}
                                </span>
                              </div>
                              {row.refund_summary.transaction_fee_retained >
                                0 && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-muted-foreground">
                                    Transaction fee retained
                                  </span>
                                  <span className="ml-auto">
                                    {formatCurrency(
                                      row.refund_summary
                                        .transaction_fee_retained,
                                      2,
                                    )}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center gap-1.5 pt-0.5 border-t border-dashed mt-0.5">
                                <span className="text-muted-foreground font-medium">
                                  Net charged
                                </span>
                                <span className="ml-auto font-semibold">
                                  {formatCurrency(
                                    Math.max(
                                      0,
                                      fees.total -
                                        row.refund_summary.amount_refunded,
                                    ),
                                    2,
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Outstanding admin-recorded charges with their own Stripe pay action —
          kept on the Financials side. The read-only ledger-history mirror is
          only rendered in standalone ('all') mode; in the split layout the
          consolidated transaction ledger already covers settled history. */}
      {showBalance && (
        <ManualChargesCard manualCharges={outstanding?.manual_charges} />
      )}

      {view === 'all' && <ManualLedgerHistoryCard />}

      <PayNowDialog
        open={singlePayId !== null}
        onClose={closeSingle}
        registrationId={singlePayId ?? ''}
        eventName={singlePayRow?.trig_event_name ?? undefined}
        // Use the breakdown's computed total — it accounts for the
        // paid_for_partner doubling even when the stored payment_amount
        // is stale (pre-fix Pay Later / Cash registrations). The backend's
        // /pay-now/:id endpoint independently recomputes from breakdown
        // for paid_for_partner rows, so this fallback can't drift from
        // the actual mint amount.
        fallbackTotal={
          singlePayRow
            ? computeFeeBreakdown(singlePayRow, { unpaid: true }).total || null
            : null
        }
        transactionFeeTotal={singlePayTransactionFee}
        onSuccess={closeSingle}
      />

      <BulkPayDialog
        open={bulkPayIds !== null}
        onClose={closeBulk}
        registrationIds={bulkPayIds ?? []}
        manualChargeAdjustmentIds={bulkManualIds}
        fallbackTotal={bulkPayTotal}
        transactionFeeTotal={bulkPayTransactionFee}
        onSuccess={closeBulk}
      />

      {/* ACH-verification warning: shown when the user clicks Pay Now on an
          entry whose current payment is a bank debit still awaiting
          microdeposit verification. Continuing with a different method
          cancels that pending ACH (backend supersede-on-complete), so we make
          the trade-off explicit before opening checkout. */}
      <Dialog
        open={verifyWarnId !== null}
        onOpenChange={(next) => {
          if (!next) setVerifyWarnId(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <DialogTitle>Bank verification still pending</DialogTitle>
            </div>
            <DialogDescription>
              {verifyWarnRow?.trig_event_name
                ? `This entry (${verifyWarnRow.trig_event_name}) has a bank `
                : 'This entry has a bank '}
              (ACH) payment that's still waiting for microdeposit verification.
              If you continue with a different payment method, that pending ACH
              will be cancelled so you aren't charged twice. You can also close
              this and finish verifying your bank instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setVerifyWarnId(null)}>
              Keep waiting
            </Button>
            <Button
              onClick={() => {
                const id = verifyWarnId;
                setVerifyWarnId(null);
                if (id) startPayOne(id);
              }}
            >
              Continue with new payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Inline fee breakdown — entry × N + transaction × N + non-member surcharge.
 *  When `owedOverride` is provided and is strictly less than `fees.total` we
 *  also render "Already paid" + "Outstanding" lines under the total so the
 *  user can see why the supplemental Pay Now amount is smaller than the
 *  full row total — e.g. they paid $55 originally and only owe a $25
 *  surcharge that was backfilled after a membership rejection. */
function FeeBreakdownLine({
  fees,
  owedOverride = null,
}: {
  fees: FeeBreakdown;
  owedOverride?: number | null;
}) {
  const hasComponents =
    fees.entry > 0 || fees.transaction > 0 || fees.nonMember > 0;
  if (!hasComponents && fees.total <= 0) return null;
  // Treat the override as authoritative for the supplemental case but
  // only when it's a partial diff (positive AND below the row total).
  const showSupplementalSplit =
    owedOverride != null &&
    owedOverride > 0 &&
    owedOverride < fees.total;
  const alreadyPaid = showSupplementalSplit ? fees.total - owedOverride! : 0;
  if (!hasComponents) {
    // No component data — fall back to just the total.
    return (
      <div className="text-xs flex items-center gap-1.5 pt-1 border-t mt-1">
        <span className="text-muted-foreground">Total</span>
        <span className="ml-auto font-semibold">
          {formatCurrency(fees.total, 2)}
        </span>
      </div>
    );
  }
  // × 2 suffix on entry / transaction lines for paid_for_partner rows so
  // the displayed total clearly traces back to the original charge.
  // Non-member surcharge gets its OWN suffix flag because it's
  // asymmetric — only the side(s) without membership contribute, so a
  // member primary covering a non-member partner shouldn't render
  // "Non-member surcharge × 2 $25" (which falsely implies $12.50 each).
  const partnerSuffix = fees.legacyDoubled ? ' × 2' : '';
  const nonMemberSuffix = fees.nonMemberDoubled ? ' × 2' : '';
  return (
    <div className="text-xs space-y-0.5 pt-1 border-t mt-1">
      {fees.paidForPartner && (
        <div className="flex items-center gap-1.5 mb-1">
          <span className="inline-flex items-center gap-1 rounded bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-medium">
            Paying for partner — fees doubled
          </span>
        </div>
      )}
      {fees.entry > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">
            Entry fee{partnerSuffix}
          </span>
          <span className="ml-auto">{formatCurrency(fees.entry, 2)}</span>
        </div>
      )}
      {fees.nonMember > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">
            Non-member surcharge{nonMemberSuffix}
          </span>
          <span className="ml-auto">
            {formatCurrency(fees.nonMember, 2)}
          </span>
        </div>
      )}
      {fees.transaction > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">
            Transaction fee{partnerSuffix}{' '}
            <span className="text-[10px]">(non-refundable)</span>
          </span>
          <span className="ml-auto">
            {formatCurrency(fees.transaction, 2)}
          </span>
        </div>
      )}
      {/* Partner-swap adjustment fee — only ever non-zero on a partner-pay
          primary row or a swapped team-partner row (computeFeeBreakdown
          folds any leftover on other rows into the transaction fee, so it
          can never be mislabelled here). */}
      {fees.swapFee > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">
            Partner-swap fee{' '}
            <span className="text-[10px]">(non-refundable)</span>
          </span>
          <span className="ml-auto">{formatCurrency(fees.swapFee, 2)}</span>
        </div>
      )}
      <div className="flex items-center gap-1.5 pt-0.5">
        <span className="text-muted-foreground font-medium">Total</span>
        <span className="ml-auto font-semibold">
          {formatCurrency(fees.total, 2)}
        </span>
      </div>
      {showSupplementalSplit && (
        <>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Already paid</span>
            <span className="ml-auto text-muted-foreground">
              −{formatCurrency(alreadyPaid, 2)}
            </span>
          </div>
          <div className="flex items-center gap-1.5 pt-0.5 border-t">
            <span className="text-amber-700 dark:text-amber-300 font-medium">
              Outstanding
            </span>
            <span className="ml-auto font-semibold text-amber-700 dark:text-amber-300">
              {formatCurrency(owedOverride!, 2)}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={`truncate ${
          bold ? 'font-semibold text-foreground' : 'text-foreground/90'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

/**
 * Inline cancellation footer. Three shapes:
 *   - paid registration with a successful refund: show refunded/retained amounts
 *   - paid registration with refund pending or failed: show captured + status
 *   - unpaid registration: show "no payment was made — fees cancelled"
 *
 * Rendered ONLY when the row is in `cancelled` status, so we don't have to
 * guard against rows that just happen to have a stale refund_summary.
 */
function CancellationSummary({
  row,
  fees,
}: {
  row: RegistrationRow;
  fees: FeeBreakdown;
}) {
  const refund = row.refund_summary;
  const wasPaid =
    !!row.payment_id &&
    (row.payment_status === 'completed' ||
      row.payment_status === 'refunded' ||
      (refund && refund.paid_total > 0));

  if (!wasPaid) {
    // Cancelled before any money changed hands — common path for
    // pending_payment cash/pay-later rows. Fees are voided, no refund
    // is owed.
    return (
      <div className="text-xs space-y-0.5 pt-1.5 mt-1.5 border-t border-dashed">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <span className="font-medium text-foreground/80">
            Cancelled
          </span>
          <span className="ml-auto">
            {fees.total > 0
              ? `${formatCurrency(fees.total, 2)} voided — no payment was made`
              : 'No payment was made'}
          </span>
        </div>
        {row.refund_summary && (
          <div className="text-muted-foreground italic">
            (Backend reports a refund record despite no completed
            payment — please refresh; if the issue persists, contact
            support.)
          </div>
        )}
      </div>
    );
  }

  // Paid path — render the refund breakdown when the metadata gives it to
  // us. If refund is pending/failed, show what we know without making up
  // numbers.
  const refundedSuccessfully =
    refund &&
    (refund.status === 'completed' || refund.status === 'COMPLETED') &&
    refund.amount_refunded > 0;

  return (
    <div className="text-xs space-y-0.5 pt-1.5 mt-1.5 border-t border-dashed">
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-foreground/80">Cancellation</span>
      </div>
      {refundedSuccessfully && refund && (
        <>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Refunded</span>
            <span className="ml-auto font-medium text-green-700 dark:text-green-400">
              {formatCurrency(refund.amount_refunded, 2)}
            </span>
          </div>
          {refund.transaction_fee_retained > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">
                Transaction fee retained (non-refundable)
              </span>
              <span className="ml-auto">
                {formatCurrency(refund.transaction_fee_retained, 2)}
              </span>
            </div>
          )}
        </>
      )}
      {refund && !refundedSuccessfully && (
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">
            Refund {refund.status?.toLowerCase() || 'pending'}
          </span>
          <span className="ml-auto">
            {formatCurrency(refund.amount_refunded, 2)}
          </span>
        </div>
      )}
      {!refund && (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <span>
            Payment captured but no refund record was found — contact
            support if you expected a refund.
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Manual ledger card ──────────────────────────────────────────────────────
//
// Surfaces the user's outstanding-but-not-yet-paid admin-recorded charges
// (committee fronted entry fee, etc.) and lets them settle via Stripe in a
// single PaymentIntent that covers the net balance. The card hides itself
// when there's nothing to show — no balance, no credits — to avoid
// cluttering the Funds tab for users who have never been touched by the
// manual ledger.

export function ManualChargesCard({
  manualCharges,
  userId,
  readOnly = false,
}: {
  manualCharges: ManualChargesBalance | undefined;
  /** Admin: render THIS contestant's ledger (reads their admin-scoped
   *  manual-ledger endpoint). Omit for the signed-in user's own card. */
  userId?: string;
  /** Hide the contestant-only pay action (admins settle via their own
   *  toolbar). */
  readOnly?: boolean;
}) {
  const [stripeOpen, setStripeOpen] = useState(false);
  // Per-org collapse state — orgs start collapsed showing just their balance;
  // expand to a scrollable entry list.
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(() => new Set());
  const toggleOrg = (key: string) =>
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const initiate = useInitiateManualCharges();
  const confirm = useConfirmPayment();
  // Full ledger history (settled + unsettled) so the card persists after the
  // balance is paid off — the manual ledger should always be visible, not
  // vanish to nothing once everything's settled. `userId` switches to the
  // admin-scoped read when an admin is viewing a contestant.
  const { data: ledgerData } = useMyManualLedger(userId);

  // Bulk-mint the PaymentIntent on demand. Manual-charge flow is still
  // eager-mint (the deferred refactor only landed for event registrations).
  // The dialog expects quote-then-confirm shape, so we stash the eager
  // result and replay its client_secret via confirmIntent. Trade-off:
  // opening this card's checkout and bailing leaves an orphan PI; the
  // event-entry flow no longer has this problem. Migrating
  // /payment-ops/initiate-manual-charges to deferred is a follow-up.
  // Defined ABOVE the early-return so hook count stays constant across
  // renders.
  const eagerOrderRef = useRef<InitiateManualChargesResponse | null>(null);

  const quoteOrder = useCallback(async () => {
    const res = await initiate.mutateAsync({});
    eagerOrderRef.current = res;
    return {
      payment_id: res.payment_id,
      stripe_publishable_key: res.stripe_publishable_key ?? undefined,
      total_amount: res.amount,
    };
  }, [initiate]);

  const confirmIntent = useCallback(
    async (_paymentId: string, _method: 'card' | 'us_bank_account') => {
      const stashed = eagerOrderRef.current;
      if (!stashed) {
        throw new Error('Manual-charge order is not loaded yet');
      }
      return {
        client_secret: stashed.client_secret,
        stripe_payment_intent_id: stashed.stripe_payment_intent_id,
        total_amount: stashed.amount,
        transaction_fee_total: 0,
        stripe_publishable_key: stashed.stripe_publishable_key ?? '',
      };
    },
    [],
  );

  const onApprove = useCallback(
    async (paymentIntentId: string) => {
      await confirm.mutateAsync({ payment_intent_id: paymentIntentId });
    },
    [confirm],
  );

  // Render-skip when the user has zero unsettled rows AND zero credit on
  // file. A non-zero credits-only state IS worth showing (so the user can
  // see "you have $X credit applied to future entries") but right now we
  // only render the actionable owed-balance flow; pure-credit visibility
  // can come in Phase 3. Placed AFTER all hooks above to satisfy the
  // Rules of Hooks — early return before the useCallbacks would change
  // the hook count between renders.
  // Full ledger history (settled + unsettled). The card persists whenever the
  // user has ANY ledger activity — it should NOT vanish once the balance is
  // paid off. Redemptions (credit spent at checkout) also keep it visible.
  const fullEntries = ledgerData?.entries ?? [];
  const redemptions = manualCharges?.redemptions ?? [];
  if (
    !manualCharges ||
    (fullEntries.length === 0 &&
      redemptions.length === 0 &&
      manualCharges.unsettled.length === 0)
  ) {
    return null;
  }

  const round = (n: number) => Math.round(n * 100) / 100;
  const owed = manualCharges.net_owed;
  const hasOutstanding = owed > 0;

  // Group ALL entries by org so the ledger reads per-org and persists across
  // settlement. Each org folds in admin debits (charges), admin credits
  // (grants), and credit REDEMPTIONS (credit the user spent at event
  // checkout). Each adjustment carries its settled state so paid-off entries
  // stay visible with a "Paid" marker instead of disappearing.
  type LedgerLine = {
    id: string;
    created_at: string;
    kind: 'debit' | 'credit' | 'redemption' | 'payment';
    typeLabel: string;
    amount: number;
    reason: string | null;
    settled: boolean;
    // Computed below by the per-org coverage pass — reflects netting between
    // unsettled charges and credits (a charge a credit covers isn't
    // "Outstanding"; a partly-consumed credit isn't flatly "Available").
    status?: { label: string; cls: string };
  };
  const byOrg = new Map<
    string,
    {
      name: string;
      // Lifetime sums (settled + unsettled) — drive the stat cards so they
      // match every visible entry.
      lifetimeDebits: number;
      lifetimeCredits: number;
      // Outstanding (unsettled) sums — drive the per-org balance.
      unsettledDebits: number;
      unsettledCredits: number;
      redemptions: number;
      payments: number;
      lines: LedgerLine[];
    }
  >();
  const groupFor = (name: string) => {
    const g = byOrg.get(name) ?? {
      name,
      lifetimeDebits: 0,
      lifetimeCredits: 0,
      unsettledDebits: 0,
      unsettledCredits: 0,
      redemptions: 0,
      payments: 0,
      lines: [],
    };
    byOrg.set(name, g);
    return g;
  };
  for (const e of fullEntries) {
    const name = e.organization_name?.trim() || 'Organization';
    const g = groupFor(name);
    const settled = !!e.settled_at;
    if (e.direction === 'debit') {
      g.lifetimeDebits += e.amount;
      if (!settled) g.unsettledDebits += e.amount;
    } else {
      g.lifetimeCredits += e.amount;
      if (!settled) g.unsettledCredits += e.amount;
    }
    g.lines.push({
      id: e.id,
      created_at: e.created_at,
      kind: e.direction === 'debit' ? 'debit' : 'credit',
      typeLabel: e.type,
      amount: e.amount,
      reason: e.reason,
      settled,
    });
  }
  for (const r of redemptions) {
    const name = r.organization_name?.trim() || 'Organization';
    const g = groupFor(name);
    g.redemptions += r.amount;
    const where = [r.event_name, r.rodeo_name].filter(Boolean).join(' at ');
    g.lines.push({
      id: `redemption:${r.id}`,
      created_at: r.created_at,
      kind: 'redemption',
      typeLabel: 'Credit applied',
      amount: r.amount,
      reason: where ? `Applied to ${where}` : 'Applied at event checkout',
      settled: false,
    });
  }
  for (const p of manualCharges.payments ?? []) {
    const name = p.organization_name?.trim() || 'Organization';
    const g = groupFor(name);
    g.payments += p.amount;
    g.lines.push({
      id: `payment:${p.id}`,
      created_at: p.created_at,
      kind: 'payment',
      typeLabel: 'Payment',
      amount: p.amount,
      reason: 'Paid toward ledger balance',
      settled: true,
    });
  }
  // Oldest-first within each org so the running story reads top-to-bottom.
  for (const g of byOrg.values()) {
    g.lines.sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
  }
  const orgGroups = Array.from(byOrg.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  // Credit spent at checkout (redemptions) reduces available credit, but only
  // the portion NOT already absorbed by settled credits — once the credits that
  // funded a redemption are settled, it's accounted for and must stop eating
  // into NEW credit. liveRedemptions = max(0, redemptions − settledCredits).
  const orgLiveRedemptions = (g: (typeof orgGroups)[number]) =>
    Math.max(0, g.redemptions - (g.lifetimeCredits - g.unsettledCredits));

  // Per-org outstanding balance: debits still owed, less the credit still on
  // file (unsettled credit minus what's been spent at checkout, floored at 0).
  const orgBalance = (g: (typeof orgGroups)[number]) =>
    round(
      g.unsettledDebits -
        Math.max(0, g.unsettledCredits - orgLiveRedemptions(g)),
    );

  // Per-line status pass. Within each org, available credit (unsettled credit
  // less live redemptions) auto-covers unsettled charges — no payment needed —
  // so a covered charge reads "Covered", not "Outstanding", and the credit that
  // covers it is shown as partly/fully used rather than fully "Available".
  // Oldest-first allocation (g.lines are already date-sorted).
  const STATUS_CLS = {
    emerald:
      'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    amber:
      'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    sky: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30',
    muted: 'bg-muted text-muted-foreground border-border',
  };
  for (const g of orgGroups) {
    let chargePool = Math.max(0, g.unsettledCredits - orgLiveRedemptions(g));
    const initialPool = chargePool;
    // Charges: cover from the available-credit pool.
    for (const line of g.lines) {
      if (line.kind !== 'debit' || line.settled) continue;
      const covered = Math.min(chargePool, line.amount);
      chargePool = round(chargePool - covered);
      line.status =
        covered >= line.amount - 0.005
          ? { label: 'Covered', cls: STATUS_CLS.emerald }
          : covered > 0.005
            ? { label: 'Partly covered', cls: STATUS_CLS.amber }
            : { label: 'Outstanding', cls: STATUS_CLS.amber };
    }
    // Credits: consumed = redemptions (live) + the charge coverage just spent.
    let consumed = round(orgLiveRedemptions(g) + (initialPool - chargePool));
    for (const line of g.lines) {
      if (line.kind !== 'credit' || line.settled) continue;
      const used = Math.min(consumed, line.amount);
      consumed = round(consumed - used);
      line.status =
        used >= line.amount - 0.005
          ? { label: 'Used', cls: STATUS_CLS.muted }
          : used > 0.005
            ? { label: 'Partly available', cls: STATUS_CLS.sky }
            : { label: 'Available', cls: STATUS_CLS.emerald };
    }
    // Everything else: settled charges, settled credits, redemptions, payments.
    for (const line of g.lines) {
      if (line.status) continue;
      line.status =
        line.kind === 'redemption'
          ? { label: 'Applied', cls: STATUS_CLS.muted }
          : line.kind === 'payment'
            ? { label: 'Paid', cls: STATUS_CLS.sky }
            : line.kind === 'debit'
              ? { label: 'Paid', cls: STATUS_CLS.emerald }
              : { label: 'Used', cls: STATUS_CLS.muted };
    }
  }

  // Stat cards: lifetime totals (match every visible entry) + the live signed
  // balance. CHARGES/CREDITS are lifetime; CREDIT APPLIED is lifetime credit
  // spent; LEDGER BALANCE is what's outstanding right now (sum of per-org).
  const lifetimeDebits = round(
    orgGroups.reduce((s, g) => s + g.lifetimeDebits, 0),
  );
  const lifetimeCredits = round(
    orgGroups.reduce((s, g) => s + g.lifetimeCredits, 0),
  );
  const creditApplied = round(redemptions.reduce((s, r) => s + r.amount, 0));
  const lifetimePaid = round(
    (manualCharges.payments ?? []).reduce((s, p) => s + p.amount, 0),
  );
  const ledgerBalance = round(orgGroups.reduce((s, g) => s + orgBalance(g), 0));
  const hasRedemptions = creditApplied > 0.005;
  const hasPayments = lifetimePaid > 0.005;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <CircleDollarSign className="h-5 w-5 text-primary" />
          Organization ledger
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Charges and credits an admin at one of your organizations recorded
          against your account (e.g. a fine, or a fronted entry fee). Balances
          are per organization; settle any amount owed here.
        </p>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        <div
          className={`grid grid-cols-1 gap-3 ${
            (() => {
              const n = 3 + (hasRedemptions ? 1 : 0) + (hasPayments ? 1 : 0);
              return n >= 5
                ? 'sm:grid-cols-2 lg:grid-cols-5'
                : n === 4
                  ? 'sm:grid-cols-2 lg:grid-cols-4'
                  : 'sm:grid-cols-3';
            })()
          }`}
        >
          <div className="rounded-lg border bg-card p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Charges (debits)
            </div>
            <div className="text-xl font-bold mt-1 tabular-nums text-amber-700 dark:text-amber-300">
              {formatCurrency(lifetimeDebits)}
            </div>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Credits
            </div>
            <div className="text-xl font-bold mt-1 tabular-nums text-emerald-700 dark:text-emerald-300">
              {formatCurrency(lifetimeCredits)}
            </div>
          </div>
          {hasRedemptions && (
            <div className="rounded-lg border bg-card p-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Credit applied
              </div>
              <div className="text-xl font-bold mt-1 tabular-nums text-foreground/70">
                {formatCurrency(creditApplied)}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                spent on event entries
              </div>
            </div>
          )}
          {hasPayments && (
            <div className="rounded-lg border bg-card p-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Paid
              </div>
              <div className="text-xl font-bold mt-1 tabular-nums text-sky-700 dark:text-sky-300">
                {formatCurrency(lifetimePaid)}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                settled via Stripe
              </div>
            </div>
          )}
          <div className="rounded-lg border bg-card p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Ledger balance
            </div>
            <div
              className={`text-xl font-bold mt-1 tabular-nums ${
                ledgerBalance > 0.005
                  ? 'text-amber-700 dark:text-amber-300'
                  : ledgerBalance < -0.005
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-foreground'
              }`}
            >
              {formatCurrency(Math.abs(ledgerBalance))}
              <span className="text-xs font-normal text-muted-foreground ml-1">
                {ledgerBalance > 0.005
                  ? 'owed'
                  : ledgerBalance < -0.005
                    ? 'credit'
                    : 'settled'}
              </span>
            </div>
          </div>
        </div>

        {/* Explicit reconciliation so the cards visibly add up — charges, less
            credits granted, plus credit spent elsewhere, less what's been
            paid, equals the current balance. */}
        <p className="text-xs text-muted-foreground">
          Charges {formatCurrency(lifetimeDebits)} − Credits{' '}
          {formatCurrency(lifetimeCredits)}
          {hasRedemptions
            ? ` + Credit applied ${formatCurrency(creditApplied)}`
            : ''}
          {hasPayments ? ` − Paid ${formatCurrency(lifetimePaid)}` : ''} ={' '}
          <span
            className={`font-medium ${
              ledgerBalance > 0.005
                ? 'text-amber-700 dark:text-amber-300'
                : 'text-foreground'
            }`}
          >
            {formatCurrency(Math.abs(ledgerBalance))}{' '}
            {ledgerBalance > 0.005
              ? 'owed'
              : ledgerBalance < -0.005
                ? 'credit'
                : 'settled'}
          </span>
          .
        </p>

        {/* Per-org collapsible groups. Collapsed = org name + its balance;
            expand = scrollable list of that org's entries. */}
        <div className="space-y-2">
          {orgGroups.map((g) => {
            const bal = orgBalance(g);
            const open = expandedOrgs.has(g.name);
            return (
              <div key={g.name} className="rounded-lg border overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleOrg(g.name)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors text-left"
                  aria-expanded={open}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {open ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="font-medium truncate">{g.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({g.lines.length})
                    </span>
                  </span>
                  <span
                    className={`tabular-nums font-semibold shrink-0 ${
                      bal > 0.005
                        ? 'text-amber-700 dark:text-amber-300'
                        : bal < -0.005
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-foreground'
                    }`}
                  >
                    {formatCurrency(Math.abs(bal))}{' '}
                    <span className="text-xs font-normal text-muted-foreground">
                      {bal > 0.005 ? 'owed' : bal < -0.005 ? 'credit' : 'settled'}
                    </span>
                  </span>
                </button>
                {open && (
                  <div className="max-h-64 overflow-y-auto border-t">
                    <table className="w-full text-sm">
                      <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground sticky top-0 z-10">
                        <tr>
                          <th className="text-left font-medium px-4 py-2">Date</th>
                          <th className="text-left font-medium px-4 py-2">Type</th>
                          <th className="text-left font-medium px-4 py-2">Status</th>
                          <th className="text-right font-medium px-4 py-2">Amount</th>
                          <th className="text-left font-medium px-4 py-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {g.lines.map((line) => {
                          // Sign in "what you owe" terms: credits and payments
                          // reduce owed (−); charges and redemptions increase it
                          // (+). Tone: charge amber, credit emerald, redemption
                          // neutral, payment blue (cash settled).
                          const sign =
                            line.kind === 'credit' || line.kind === 'payment'
                              ? '-'
                              : '+';
                          const tone =
                            line.kind === 'debit'
                              ? 'text-amber-700 dark:text-amber-300'
                              : line.kind === 'credit'
                                ? 'text-emerald-700 dark:text-emerald-300'
                                : line.kind === 'payment'
                                  ? 'text-sky-700 dark:text-sky-300'
                                  : 'text-foreground/70';
                          // Status is precomputed per line by the per-org
                          // coverage pass (reflects charge↔credit netting).
                          const status = line.status ?? {
                            label: '',
                            cls: 'bg-muted text-muted-foreground border-border',
                          };
                          return (
                            <tr key={line.id}>
                              <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                                {formatDateTime(line.created_at)}
                              </td>
                              <td className="px-4 py-2 capitalize">
                                {line.typeLabel}
                              </td>
                              <td className="px-4 py-2">
                                <span
                                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${status.cls}`}
                                >
                                  {status.label}
                                </span>
                              </td>
                              <td
                                className={`px-4 py-2 text-right tabular-nums font-medium ${tone}`}
                              >
                                {sign}
                                {formatCurrency(line.amount)}
                              </td>
                              <td className="px-4 py-2 text-foreground">
                                {line.reason}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {readOnly ? (
          <div className="text-sm text-muted-foreground">
            {hasOutstanding
              ? `This contestant owes ${formatCurrency(
                  owed,
                )} on their ledger. They settle it from their own Financials tab; you can also record an offline payment from the toolbar above.`
              : ledgerBalance < -0.005
                ? `This contestant has ${formatCurrency(
                    Math.abs(ledgerBalance),
                  )} in credit on file — nothing owed right now.`
                : 'Nothing outstanding on this ledger.'}
          </div>
        ) : hasOutstanding ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-muted-foreground">
              Pays the net balance in one Stripe charge. Card or ACH —
              same flow as event registrations.
            </div>
            <Button
              onClick={() => setStripeOpen(true)}
              size="sm"
              disabled={initiate.isPending}
            >
              {initiate.isPending
                ? 'Preparing…'
                : `Pay outstanding charges (${formatCurrency(owed)})`}
            </Button>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            Your credits on file cover what's owed — nothing to pay right now.
            When you enter a new event, you can choose to apply this credit at
            checkout to reduce the amount you pay.
          </div>
        )}
      </CardContent>

      {!readOnly && (
        <StripeCheckoutDialog
          open={stripeOpen}
          onClose={() => setStripeOpen(false)}
          title="Pay outstanding charges"
          amountLabel="Amount due"
          fallbackTotal={owed}
          triggerKey={manualCharges.adjustment_ids.join(',')}
          quoteOrder={quoteOrder}
          confirmIntent={confirmIntent}
          onApprove={onApprove}
        />
      )}
    </Card>
  );
}

// ─── Ledger history card ─────────────────────────────────────────────────────
//
// Read-only mirror of the user's account_adjustments rows — both
// settled and unsettled — so they can see proof of a paid manual charge
// after Stripe captures (the unpaid card empties out on settle, which
// otherwise leaves no on-page record). Renders nothing when the user
// has zero ledger rows so it doesn't clutter the page for the typical
// case (no committee charges ever filed).

function ManualLedgerHistoryCard() {
  const { data, isLoading } = useMyManualLedger();
  const entries = data?.entries ?? [];

  if (isLoading) {
    return null;
  }

  if (entries.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <CircleDollarSign className="h-5 w-5 text-primary" />
          Ledger history
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Every charge or credit a committee has recorded against your
          account, including ones you've already settled. The Manual
          charges card above only shows what's still outstanding.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-4 py-2">Date</th>
                <th className="text-left font-medium px-4 py-2">Type</th>
                <th className="text-left font-medium px-4 py-2">Organization</th>
                <th className="text-right font-medium px-4 py-2">Amount</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
                <th className="text-left font-medium px-4 py-2">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.map((row: ManualLedgerEntry) => {
                const isDebit = row.direction === 'debit';
                const sign = isDebit ? '+' : '-';
                const tone = isDebit
                  ? 'text-amber-700 dark:text-amber-300'
                  : 'text-emerald-700 dark:text-emerald-300';
                const settled = !!row.settled_at;
                return (
                  <tr key={row.id}>
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                      {new Date(row.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 capitalize">{row.type}</td>
                    <td className="px-4 py-2 text-foreground">
                      {row.organization_name ?? (
                        <span className="text-muted-foreground italic">
                          System-level
                        </span>
                      )}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums font-medium ${tone}`}
                    >
                      {sign}
                      {formatCurrency(row.amount)}
                    </td>
                    <td className="px-4 py-2">
                      <Badge
                        variant="outline"
                        className={
                          settled
                            ? 'text-xs bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                            : 'text-xs bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30'
                        }
                      >
                        {settled ? 'Paid' : 'Outstanding'}
                      </Badge>
                      {settled && row.settled_at && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {new Date(row.settled_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-foreground">{row.reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

